<template>
  <div class="c-stars" :class="{ '-values': displayValues }" @mouseleave="onMouseLeave">
    <template v-if="!compact">
      <template v-if="isCustomStar">
        <span
          v-for="(item, index) in max"
          ref="customStars"
          :key="item.id"
          :class="classes(item.id)"
          @mouseenter="showTooltip[index] = true; onMouseEnter(item.id, item.status)"
          @mouseleave="showTooltip[index] = false"
          @click="onChange(item.id)"
        >
          <c-tooltip
            dark
            position="top"
            :show="showTooltip[index]"
            :element-spacing="5"
            :target="Array.isArray($refs.customStars)
              ? $refs.customStars[index]
              : $refs.customStars"
          >
            <template v-slot:default>
              <component
                v-if="item?.customMessage"
                :is="item?.customMessage"
                v-bind="item.props"
              />

              <span v-else>
                {{ item.message }}
              </span>
            </template>
          </c-tooltip>

          <div class="mask" :style="{ width: getMaskWidth(item.id) }">
            <c-icon
              icon="star-favorite-filled"
              class="-custom"
              :class="customStar"
            />
          </div>

          <c-icon
            icon="star-favorite"
            :class="customStar"
          />

          <span v-if="displayValues" class="value">{{ item.id }}</span>
        </span>
      </template>

      <template v-else>
        <span
          v-for="i in max"
          :key="i"
          :class="classes(i)"
          @mouseenter="onMouseEnter(i)"
          @click="onChange(i)"
        >
          <div class="mask" :style="{ width: getMaskWidth(i) }">
            <c-icon icon="star-favorite-filled" />
          </div>

          <c-icon icon="star-favorite" />

          <span v-if="displayValues" class="value">{{ i }}</span>
        </span>
      </template>
    </template>

    <template v-else>
      <span :class="[ 'compact-star', { '-active': currentValue > 0 } ]">
        {{ currentValue }} / {{ total }} <c-icon :icon="getIcon(currentValue)" />
      </span>
    </template>
  </div>
</template>

<script>
import { MediaQuery } from '@convenia/mixins'
import CIcon from '@convenia/components/CIcon/index.vue'
import CTooltip from '@convenia/components/CTooltip/index.vue'

export default {
  name: 'CStars',

  mixins: [ MediaQuery ],

  components: {
    CIcon,
    CTooltip
  },

  props: {
    value: {
      type: Number,
      default: undefined
    },

    /**
     * Defines the rating scale for the star component.
     *
     * This prop can accept either:
     * - A **Number**: the maximum number of stars to display (e.g., `5`).
     * - An **Array**: a list of custom star configuration objects. Each object can include:
     *    - `id` {Number} — The numeric value of the star.
     *    - `message` {String} — A simple text label for the tooltip (optional).
     *    - `customMessage` {Component} — A Vue component to render
     *      a fully customized tooltip (optional).
     *    - `props` {Object} — Props to be passed to the custom component (optional).
     *    - `status` {String} — An optional status tag (e.g., `'success'`, `'alert'`, `'error'`).
     *
     * Example:
     * ```js
     * max: [
     *   { id: 1, message: 'Poor' },
     *   { id: 2, customMessage: CustomTooltip,
     *      props: { title: 'Fair', description: 'Slightly below expectations' } },
     *   { id: 3, message: 'Good' },
     *   { id: 4, message: 'Very Good' },
     *   { id: 5, message: 'Excellent' }
     * ]
     * ```
     *
     * @type {number | Array<{
     *  id: number,
     *  message?: string,
     *  customMessage?: Component,
     *  props?: Object,
     *  status?: string
     *  }>}
     * @default 5
     */
    max: {
      type: [ Number, Array ],
      default: 5
    },

    size: {
      type: String,
      default: 'medium',
      validation: (value) => [ 'small', 'medium' ].includes(value),
    },

    displayValues: Boolean,
    compact: Boolean,
    readyOnly: Boolean,
    disabled: Boolean
  },

  emits: [
    'input'
  ],

  data: () => ({
    hovering: -1,
    hoveredStatus: null,
    showTooltip: []
  }),

  mounted () {
    this.verifyStatus()
  },

  computed: {
    currentValue () {
      return this.value
    },

    customStar () {
      return [
        {
          '-error': [ 'error' ].includes(this.hoveredStatus),
          '-alert': [ 'alert' ].includes(this.hoveredStatus),
          '-success': [ 'success' ].includes(this.hoveredStatus),
        }
      ]
    },

    total () {
      return this.isCustomStar ? this.max.length : this.max
    },

    isCustomStar () {
      return Array.isArray(this.max)
    }
  },

  methods: {
    classes (i) {
      return [
        'star',
        {
          '-hovering': this.hovering >= i,
          '-active': Math.ceil(this.currentValue) >= i,
          '-no-cursor': this.readyOnly,
          '-disabled': this.disabled,
          '-error': [ 'error' ].includes(this.hoveredStatus),
          '-alert': [ 'alert' ].includes(this.hoveredStatus),
          '-success': [ 'success' ].includes(this.hoveredStatus),
          '-small': this.size === 'small',
          '-medium': this.size === 'medium',
        }
      ]
    },

    onMouseEnter (value, status) {
      this.hovering = !this.readyOnly ? value : -1
      this.hoveredStatus = status
    },

    onMouseLeave () {
      this.hovering = -1
      this.verifyStatus()
    },

    onChange (value) {
      this.$emit('input', value)
    },

    getMaskWidth (index) {
      if (this.currentValue == null)
        return '0%'

      const factor = (this.currentValue + 1) - index

      return `${Math.min(Math.max(0.0, factor * 100), 100)}%`
    },

    getIcon (value) {
      if (this.disabled)
        return 'star-favorite-filled'

      if (this.currentValue >= value)
        return 'star-favorite-filled'

      if (this.hovering < value)
        return 'star-favorite'

      return 'star-favorite-filled'
    },

    verifyStatus () {
      if (this.isCustomStar) {
        const item = this.max.find(item => item.id === Math.ceil(this.currentValue))

        this.hoveredStatus = item?.status
      }
    }
  }
}
</script>

<style lang="scss">
.c-stars {
  display: flex;
  flex-flow: row wrap;

  & > .star {
    cursor: pointer;
    position: relative;
    margin: 0 5px;
    height: 40px;
    display: flex;
    align-items: center;

    &.-no-cursor {
      cursor: none;
      pointer-events: none;
    }

    & > .mask {
      overflow: hidden;
      position: absolute;
      display: flex;
      align-items: flex-end;
      left: 0;

      & > .c-icon { flex-shrink: 0; }
    }

    & > .c-icon,
    & > .mask > .c-icon {
      stroke: color-var(text, base-30);
      fill: color-var(text, base-30);
    }

    & > .value {
      @include typo(body-1);
    }

    &.-active {
      &:after {
        content: "";
        position: absolute;
        width: 60px;
        height: 60px;
        border-radius: 60px;
        transform: translateX(-50%);
        top: -10px;
        left: 50%;
        pointer-events: none;
      }

      & > .c-icon,
      & > .mask > .c-icon {
        stroke: orange;
        fill: orange;

        &.-error {
          stroke: color-var(negative);
          fill: color-var(negative);
        }

        &.-alert {
          stroke: color-var(alert);
          fill: color-var(alert);
        }

        &.-success {
          stroke: color-var(positive);
          fill: color-var(positive);
        }
      }
    }

    &:after {
      opacity: 0.1;
    }

    &.-active {
      &:after {
        background: radial-gradient(circle at center, orange 0%, rgba(orange, 0) 60%);
      }
    }

    &.-error:after {
      background: radial-gradient(circle at center,
        color-var(negative) 0%, transparent 60%);
    }

    &.-alert:after {
      background: radial-gradient(circle at center,
        color-var(alert) 0%, transparent 60%);
    }

    &.-success:after {
      background: radial-gradient(circle at center,
        color-var(positive) 0%, transparent 60%);
    }

    &.-hovering:not(.-active) {
      & > .c-icon {
        stroke: orange;
        fill: orange;
        opacity: .5;

        &.-error {
          stroke: color-var(negative);
          fill: color-var(negative);
        }

        &.-alert {
          stroke: color-var(alert);
          fill: color-var(alert);
        }

        &.-success {
          stroke: color-var(positive);
          fill: color-var(positive);
        }
      }
    }

    &.-hovering, &.-active {
      & > .c-icon.-custom,
      & > .mask > .c-icon { transform: scale(1.2); }
    }

    &.-disabled {
      cursor: none;
      pointer-events: none;

      & > .c-icon,
      & > .mask > .c-icon {
        stroke: color-var(text, base-10);
        fill: color-var(text, base-10);
      }
    }

    &.-small {
      transform: scale(0.8);
      margin: 0 -3px 0 0;
    }
  }

  & > .compact-star {
    display: flex;
    justify-content: center;
    align-items: center;

    &.-active > .c-icon {
      stroke: orange;
      fill: orange;
    }
  }

  &.-values {
    margin-bottom: 10px;
    & > .star {
      margin: 0 10px;
      align-items: center;
      justify-content: space-between;
      height: 60px;
      flex-flow: column;
    }
  }
}
</style>
